import { Rocket, ShieldAlert } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PageHeader } from '@/components/common/PageHeader'
import { Pager } from '@/components/common/Pager'
import { EmptyState, ErrorState } from '@/components/common/States'
import { usePagination } from '@/components/common/usePagination'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table'
import { fmtDateTime } from '../shared/format'
import { useAuth } from '@/lib/auth/AuthProvider'
import type { Release } from '../shared/types'
import { RELEASE_WRITE_PERM } from '../updates/api'
import { PublishReleaseModal } from '../updates/PublishReleaseModal'
import { publishable } from '../updates/rules'
import { useReleases } from './api'
import { PanelReleasesCard } from './PanelReleasesCard'

export function ReleasesPage() {
  const { t } = useTranslation()
  const releases = useReleases()
  const { pageRows, pager } = usePagination(releases.data ?? [])
  const { can } = useAuth()
  const [publishing, setPublishing] = useState<Release | null>(null)
  const canPublish = can(RELEASE_WRITE_PERM)
  return (
    <>
      <PageHeader title={t('releases.title')} description={t('releases.subtitle')} />
      <Card>
        {releases.isError ? (
          <ErrorState error={releases.error} onRetry={() => void releases.refetch()} />
        ) : (
          <>
            <Table>
              <THead>
                <TR className="hover:bg-transparent">
                  <TH>{t('releases.columns.id')}</TH>
                  <TH>{t('releases.columns.jobType')}</TH>
                  <TH>{t('releases.columns.status')}</TH>
                  <TH className="hidden md:table-cell">{t('releases.columns.image')}</TH>
                  <TH>{t('releases.columns.version')}</TH>
                  <TH className="hidden sm:table-cell">{t('releases.columns.created')}</TH>
                  <TH className="text-right">{t('releases.columns.actions')}</TH>
                </TR>
              </THead>
              <TBody>
                {releases.isLoading
                  ? Array.from({ length: 5 }, (_, i) => (
                      <TR key={i}>
                        <TD colSpan={7}>
                          <Skeleton className="h-6" />
                        </TD>
                      </TR>
                    ))
                  : pageRows.map((r) => (
                      <TR key={r.id}>
                        <TD className="font-mono text-xs">{r.id.slice(0, 8)}</TD>
                        <TD>{r.job_type}</TD>
                        <TD>
                          <Badge tone={r.status === 'done' ? 'success' : r.status === 'failed' ? 'danger' : 'neutral'}>
                            {r.status}
                          </Badge>
                        </TD>
                        <TD className="text-muted-foreground hidden max-w-xs text-xs md:table-cell">
                          <div className="truncate">{r.image_ref || '—'}</div>
                          {r.image_digest ? (
                            <Badge tone={r.cosign_verified ? 'success' : 'warning'} className="mt-1">
                              {t(r.cosign_verified ? 'releases.signed' : 'releases.unsigned')}
                            </Badge>
                          ) : null}
                        </TD>
                        <TD>
                          {r.published_at ? (
                            <span className="inline-flex items-center gap-1.5" title={r.release_notes || undefined}>
                              <span className="font-medium">{r.version}</span>
                              {r.security ? (
                                <ShieldAlert className="text-destructive size-4" aria-label={t('releases.security')} />
                              ) : null}
                              <span className="text-muted-foreground text-xs tabular-nums">
                                {fmtDateTime(r.published_at)}
                              </span>
                            </span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TD>
                        <TD className="text-muted-foreground hidden tabular-nums sm:table-cell">
                          {fmtDateTime(r.created_at)}
                        </TD>
                        <TD className="text-right">
                          {canPublish && publishable(r) ? (
                            <Button size="sm" variant="outline" onClick={() => setPublishing(r)}>
                              <Rocket />
                              {t('releases.publish')}
                            </Button>
                          ) : null}
                        </TD>
                      </TR>
                    ))}
              </TBody>
            </Table>
            <Pager {...pager} />
          </>
        )}
        {!releases.isLoading && !releases.isError && (releases.data ?? []).length === 0 ? <EmptyState /> : null}
      </Card>
      <PanelReleasesCard canWrite={canPublish} />
      {publishing ? <PublishReleaseModal release={publishing} onClose={() => setPublishing(null)} /> : null}
    </>
  )
}
